/** Duplicate-lead detection + manual merge (Tom 2026-08-13: "if there is a
 * duplicate I want the system to let me know there is a duplicate and which is
 * the other one ... and maybe the ability to merge it").
 *
 * Two leads for one house appear when a source gives a partial address (an
 * InvestorLift city-only pin) and a text later brings the full street. The
 * backend matches PRECISELY — same normalized address or a shared parcel —
 * never a fuzzy guess. Merge only runs on an explicit tap. */
import { backendFetch } from "../../lib/backend";

export interface DuplicateLead {
  address: string;
  price: number | null;
  source: string | null;
  parcel: string;
  createdAt: string | null;
  /** why it matched: "address" (same street) or "parcel" (same parcel id) */
  match: "address" | "parcel";
}

/** Live (non-dismissed) twins of `address`. Empty array on any error — a
 * banner that can't load must never block the drawer. */
export async function fetchDuplicates(token: string | null, address: string): Promise<DuplicateLead[]> {
  if (!address || address.trim().length < 5) return [];
  try {
    const j = await backendFetch<Record<string, unknown>>(
      token,
      `/api/records/duplicates?address=${encodeURIComponent(address)}`,
      { timeoutMs: 20000 },
    );
    const rows = Array.isArray(j?.["duplicates"]) ? (j["duplicates"] as Record<string, unknown>[]) : [];
    return rows.map((r): DuplicateLead => ({
      address: String(r["address"] ?? ""),
      price: typeof r["price"] === "number" ? (r["price"] as number) : null,
      source: typeof r["source"] === "string" ? (r["source"] as string) : null,
      parcel: String(r["parcel"] ?? ""),
      createdAt: typeof r["created_at"] === "string" ? (r["created_at"] as string) : null,
      match: r["match"] === "parcel" ? "parcel" : "address",
    })).filter((r) => r.address);
  } catch {
    return [];
  }
}

/** Fold `dropAddress` into `keepAddress`: keep's fields win, drop fills blanks,
 * children re-key onto keep, drop is tombstoned (recoverable). */
export async function mergeLeads(
  token: string | null,
  keepAddress: string,
  dropAddress: string,
): Promise<{ ok: boolean }> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/records/merge-leads", {
    method: "POST",
    body: { keep_address: keepAddress, drop_address: dropAddress },
    timeoutMs: 30000,
  });
  return { ok: j?.["ok"] === true };
}
